import { Icon, type IconName } from './Icon';

/** One choice of a segmented result control. */
export interface Segment<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon: IconName;
  /** The colour of the choice once selected. */
  readonly tone: 'ok' | 'defect' | 'neutral' | 'pending';
}

/**
 * A result as one quiet track with the choice filled (Design Gate 1.2): the same large targets as
 * separate buttons, less repetition. Each choice is a toggle button (aria-pressed); the group is
 * named after the item.
 */
export function ResultSegment<T extends string>({
  label,
  segments,
  value,
  disabled = false,
  onChoose,
}: {
  readonly label: string;
  readonly segments: readonly Segment<T>[];
  readonly value: T | null;
  readonly disabled?: boolean;
  readonly onChoose: (value: T) => void;
}) {
  return (
    <div
      className={`seg${value === null ? '' : ' seg--chosen'}`}
      role="group"
      aria-label={label}
      style={{ gridTemplateColumns: `repeat(${String(segments.length)}, minmax(0, 1fr))` }}
    >
      {segments.map((s) => (
        <button
          key={s.value}
          type="button"
          className={`seg__option seg__option--${s.tone}`}
          aria-pressed={value === s.value}
          disabled={disabled}
          onClick={() => {
            onChoose(s.value);
          }}
        >
          <Icon name={s.icon} />
          <span>{s.label}</span>
        </button>
      ))}
    </div>
  );
}
